import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { paymentsAPI } from '../lib/api';
import { getProvider, setProvider as persistProvider } from '../lib/region';

const money = (major, currency) => currency === 'USD'
  ? `$${Number(major || 0).toLocaleString('en-US')}`
  : `\u20B9${Number(major || 0).toLocaleString('en-IN')}`;
const fromMinor = (minor, currency) => (currency === 'USD'
  ? `$${Number((minor || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  : `\u20B9${Number((minor || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const CREDIT_PACK_SIZE = 100;
const CREDIT_PACK_PRICE = { USD: 20, INR: 1960 };
const BGV_CHECK_PRICE = { USD: 50, INR: 4900 };

const GEO_PRICING = [
  { region: 'India', price: '\u20b924,500', unit: 'Starter / mo' },
  { region: 'International', price: '$250', unit: 'Starter / mo' },
];

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const STATUS_STYLE = {
  paid: { bg: '#ECFDF5', color: '#059669', label: 'Paid' },
  created: { bg: '#FFFBEB', color: '#B45309', label: 'Pending' },
  verification_failed: { bg: '#FEF2F2', color: '#DC2626', label: 'Failed' },
};

export default function BillingPage() {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [creditPacks, setCreditPacks] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [bgvQty, setBgvQty] = useState(1);
  const [bgvPurchasing, setBgvPurchasing] = useState(false);
  const [provider, setProviderState] = useState(getProvider()); // 'stripe' | 'razorpay'
  const currency = provider === 'stripe' ? 'USD' : 'INR';
  const chooseProvider = (p) => { setProviderState(p); persistProvider(p); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sub, hist] = await Promise.all([
        paymentsAPI.getSubscription(),
        paymentsAPI.getHistory(),
      ]);
      setSubscription(sub.data);
      setPayments(hist.data.payments || []);
    } catch (e) {
      toast.error('Could not load billing details.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (orderId) => {
    setDownloadingId(orderId);
    try {
      const res = await paymentsAPI.downloadInvoice(orderId);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CHIRE-Invoice-${orderId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Invoice is only available for paid orders.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleResend = async (orderId) => {
    setResendingId(orderId);
    try {
      const res = await paymentsAPI.resendInvoice(orderId);
      toast.success(`Invoice emailed to ${res.data.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send the invoice email.');
    } finally {
      setResendingId(null);
    }
  };

  const handlePurchaseCredits = async () => {
    const packs = Math.max(1, parseInt(creditPacks, 10) || 1);
    setPurchasing(true);
    try {
      if (provider === 'stripe') {
        const { data } = await paymentsAPI.stripeCreateCreditSession(packs, window.location.origin);
        if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
        toast.error('Could not start the purchase. Please try again.');
        setPurchasing(false);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error('Could not load the payment gateway. Please retry.');
        setPurchasing(false);
        return;
      }
      const { data } = await paymentsAPI.createCreditOrder(packs);
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'CHIRE',
        description: data.plan_name,
        order_id: data.order_id,
        prefill: { name: data.prefill?.name || '', email: data.prefill?.email || '' },
        theme: { color: '#0076A3' },
        handler: async (resp) => {
          try {
            await paymentsAPI.verify({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`${packs * CREDIT_PACK_SIZE} credits added to your account!`);
            await load();
          } catch (e) {
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setPurchasing(false);
          }
        },
        modal: { ondismiss: () => setPurchasing(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (r) => {
        toast.error('Payment failed: ' + (r.error?.description || 'Please try again.'));
        setPurchasing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error('Could not start the purchase. Please try again.');
      setPurchasing(false);
    }
  };

  const handlePurchaseBgv = async () => {
    const qty = Math.max(1, parseInt(bgvQty, 10) || 1);
    setBgvPurchasing(true);
    try {
      if (provider === 'stripe') {
        const { data } = await paymentsAPI.stripeCreateBgvSession(qty, window.location.origin);
        if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
        toast.error('Could not start the purchase. Please try again.');
        setBgvPurchasing(false);
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) {
        toast.error('Could not load the payment gateway. Please retry.');
        setBgvPurchasing(false);
        return;
      }
      const { data } = await paymentsAPI.createBgvOrder(qty);
      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'CHIRE',
        description: data.plan_name,
        order_id: data.order_id,
        prefill: { name: data.prefill?.name || '', email: data.prefill?.email || '' },
        theme: { color: '#0076A3' },
        handler: async (resp) => {
          try {
            await paymentsAPI.verify({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`${qty} BGV check${qty === 1 ? '' : 's'} added to your account!`);
            await load();
          } catch (e) {
            toast.error('Payment verification failed. Please contact support.');
          } finally {
            setBgvPurchasing(false);
          }
        },
        modal: { ondismiss: () => setBgvPurchasing(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (r) => {
        toast.error('Payment failed: ' + (r.error?.description || 'Please try again.'));
        setBgvPurchasing(false);
      });
      rzp.open();
    } catch (e) {
      toast.error('Could not start the purchase. Please try again.');
      setBgvPurchasing(false);
    }
  };

  const isActive = subscription?.status === 'active';
  const credits = subscription?.credits || { monthly_allotment: 0, plan_credits_used: 0, plan_credits_remaining: 0, extra_credits: 0, total_available: 0 };
  const usedPct = credits.monthly_allotment > 0
    ? Math.min(100, Math.round((credits.plan_credits_used / credits.monthly_allotment) * 100))
    : 0;
  const bgv = subscription?.bgv || { monthly_allotment: 0, checks_used: 0, plan_checks_remaining: 0, extra_checks: 0, total_available: 0, checks_consumed_total: 0 };
  const bgvUsedPct = bgv.monthly_allotment > 0
    ? Math.min(100, Math.round((bgv.checks_used / bgv.monthly_allotment) * 100))
    : 0;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '2rem 1.5rem' }} data-testid="billing-page">
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.75rem', fontWeight: 700, color: '#0076A3', margin: 0 }}>Billing</h1>
        <p style={{ fontSize: '0.9375rem', color: '#718096', marginTop: '0.375rem' }}>Manage your subscription, view payment history and download invoices.</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '0.9rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#A0AEC0', fontWeight: 700 }}>Pay in</span>
          <div style={{ display: 'inline-flex', background: '#F1EAE1', borderRadius: 999, padding: '0.25rem', border: '1px solid #ECE7DE' }} data-testid="billing-provider-toggle">
            <button data-testid="billing-provider-usd" onClick={() => chooseProvider('stripe')} style={{
              padding: '0.35rem 0.95rem', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, transition: 'background 0.2s, color 0.2s',
              background: provider === 'stripe' ? '#002A38' : 'transparent', color: provider === 'stripe' ? '#fff' : '#78716C',
            }}>USD · Stripe</button>
            <button data-testid="billing-provider-inr" onClick={() => chooseProvider('razorpay')} style={{
              padding: '0.35rem 0.95rem', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, transition: 'background 0.2s, color 0.2s',
              background: provider === 'razorpay' ? '#002A38' : 'transparent', color: provider === 'razorpay' ? '#fff' : '#78716C',
            }}>INR · Razorpay</button>
          </div>
        </div>
      </div>

      {/* Current plan */}
      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.75rem', marginBottom: '1.5rem' }} data-testid="current-plan-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#A0AEC0', fontWeight: 700, marginBottom: '0.375rem' }}>Current Plan</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.5rem', fontWeight: 700, color: '#0076A3' }} data-testid="current-plan-name">
                {loading ? '…' : (subscription?.plan_name || 'No active plan')}
              </span>
              <span style={{
                fontSize: '0.75rem', fontWeight: 700, padding: '0.25rem 0.625rem', borderRadius: 999,
                background: isActive ? '#ECFDF5' : '#F1F5F9', color: isActive ? '#059669' : '#64748B',
              }} data-testid="subscription-status">
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {subscription?.updated_at && (
              <div style={{ fontSize: '0.8125rem', color: '#718096', marginTop: '0.375rem' }}>
                Since {fmtDate(subscription.updated_at)}
              </div>
            )}
          </div>
          <Button
            data-testid="manage-plan-btn"
            onClick={() => navigate('/pricing')}
            style={{ background: '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600, padding: '0.625rem 1.5rem' }}
          >
            {isActive ? 'Change Plan' : 'Choose a Plan'}
          </Button>
        </div>
      </Card>

      {/* Credit usage counter */}
      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.75rem', marginBottom: '1.5rem' }} data-testid="credit-usage-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.25rem' }}>Credit Usage</h2>
            <p style={{ fontSize: '0.8125rem', color: '#718096', margin: 0 }}>
              1 credit is used per AI interview. Monthly plan credits reset on the 1st; purchased credits roll over.
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.6875rem', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>Available now</div>
            <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.75rem', fontWeight: 800, color: '#0076A3' }} data-testid="credits-available">
              {loading ? '…' : credits.total_available}
            </div>
          </div>
        </div>

        {/* Monthly allotment progress */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: '#4A5568', marginBottom: '0.375rem' }}>
            <span>Monthly plan credits</span>
            <span data-testid="credits-monthly-label">
              <strong style={{ color: '#0076A3' }}>{credits.plan_credits_used}</strong> / {credits.monthly_allotment} used
            </span>
          </div>
          <div style={{ height: 10, borderRadius: 999, background: '#F0EBE3', overflow: 'hidden' }}>
            <div data-testid="credits-usage-bar" style={{
              height: '100%', width: `${usedPct}%`, borderRadius: 999,
              background: usedPct >= 90 ? '#DC2626' : usedPct >= 70 ? '#F59E0B' : '#0076A3',
              transition: 'width 0.5s ease-out',
            }} />
          </div>
          <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.375rem' }}>
            {credits.monthly_allotment === 0
              ? 'No active plan — choose a plan to get monthly credits.'
              : `${credits.plan_credits_remaining} plan credit${credits.plan_credits_remaining === 1 ? '' : 's'} remaining this month`}
          </div>
        </div>

        {/* Breakdown chips */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 160px', padding: '0.75rem 1rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
            <div style={{ fontSize: '0.6875rem', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>Plan remaining</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0076A3' }} data-testid="credits-plan-remaining">{credits.plan_credits_remaining}</div>
          </div>
          <div style={{ flex: '1 1 160px', padding: '0.75rem 1rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
            <div style={{ fontSize: '0.6875rem', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>Extra credits (rollover)</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0076A3' }} data-testid="credits-extra">{credits.extra_credits}</div>
          </div>
          <div style={{ flex: '1 1 160px', padding: '0.75rem 1rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
            <div style={{ fontSize: '0.6875rem', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>Used all-time</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0076A3' }} data-testid="credits-used-total">{credits.credits_consumed_total ?? 0}</div>
          </div>
        </div>
      </Card>

      {/* Buy extra credits */}
      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.75rem', marginBottom: '1.5rem' }} data-testid="buy-credits-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.25rem' }}>
          <div style={{ maxWidth: 460 }}>
            <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.375rem' }}>Buy Credits</h2>
            <p style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.5rem' }}>
              Top up beyond your monthly plan at <strong>{CREDIT_PACK_SIZE} credits for {money(CREDIT_PACK_PRICE[currency], currency)}</strong>. Purchased credits never expire.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ECE7DE', borderRadius: 10, overflow: 'hidden' }}>
              <button
                data-testid="credit-packs-decrement"
                onClick={() => setCreditPacks((q) => Math.max(1, (parseInt(q, 10) || 1) - 1))}
                style={{ background: '#FAF7F2', border: 'none', width: 38, height: 42, fontSize: '1.125rem', cursor: 'pointer', color: '#4A5568' }}
              >−</button>
              <input
                data-testid="credit-packs-input"
                type="number"
                min={1}
                value={creditPacks}
                onChange={(e) => setCreditPacks(e.target.value)}
                style={{ width: 60, height: 42, border: 'none', textAlign: 'center', fontSize: '0.9375rem', fontWeight: 700, color: '#0076A3', outline: 'none' }}
              />
              <button
                data-testid="credit-packs-increment"
                onClick={() => setCreditPacks((q) => (parseInt(q, 10) || 1) + 1)}
                style={{ background: '#FAF7F2', border: 'none', width: 38, height: 42, fontSize: '1.125rem', cursor: 'pointer', color: '#4A5568' }}
              >+</button>
            </div>
            <div style={{ textAlign: 'right', minWidth: 96 }}>
              <div style={{ fontSize: '0.75rem', color: '#A0AEC0' }} data-testid="credit-pack-credits">
                {(Math.max(1, parseInt(creditPacks, 10) || 1) * CREDIT_PACK_SIZE).toLocaleString('en-US')} credits
              </div>
              <div style={{ fontSize: '1.125rem', fontWeight: 800, color: '#0076A3' }} data-testid="credit-total">
                {money(Math.max(1, parseInt(creditPacks, 10) || 1) * CREDIT_PACK_PRICE[currency], currency)}
              </div>
            </div>
            <Button
              data-testid="buy-credits-btn"
              onClick={handlePurchaseCredits}
              disabled={purchasing}
              style={{ background: purchasing ? '#94A3B8' : '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600, padding: '0.625rem 1.5rem', cursor: purchasing ? 'wait' : 'pointer' }}
            >
              {purchasing ? 'Processing…' : 'Buy Credits'}
            </Button>
          </div>
        </div>
      </Card>

      {/* BGV check usage counter */}
      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.75rem', marginBottom: '1.5rem' }} data-testid="bgv-usage-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.25rem' }}>Background Verification Checks</h2>
            <p style={{ fontSize: '0.8125rem', color: '#718096', margin: 0 }}>
              1 check = 1 document/verification item in a request. Monthly free checks reset on the 1st; purchased checks roll over.
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.6875rem', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>Available now</div>
            <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.75rem', fontWeight: 800, color: '#0076A3' }} data-testid="bgv-available">
              {loading ? '…' : bgv.total_available}
            </div>
          </div>
        </div>

        {/* Monthly allotment progress */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', color: '#4A5568', marginBottom: '0.375rem' }}>
            <span>Monthly free checks</span>
            <span data-testid="bgv-monthly-label">
              <strong style={{ color: '#0076A3' }}>{bgv.checks_used}</strong> / {bgv.monthly_allotment} used
            </span>
          </div>
          <div style={{ height: 10, borderRadius: 999, background: '#F0EBE3', overflow: 'hidden' }}>
            <div data-testid="bgv-usage-bar" style={{
              height: '100%', width: `${bgvUsedPct}%`, borderRadius: 999,
              background: bgvUsedPct >= 90 ? '#DC2626' : bgvUsedPct >= 70 ? '#F59E0B' : '#0076A3',
              transition: 'width 0.5s ease-out',
            }} />
          </div>
          <div style={{ fontSize: '0.6875rem', color: '#94A3B8', marginTop: '0.375rem' }}>
            {bgv.monthly_allotment === 0
              ? 'No active plan — choose a plan to get monthly BGV checks.'
              : `${bgv.plan_checks_remaining} free check${bgv.plan_checks_remaining === 1 ? '' : 's'} remaining this month`}
          </div>
        </div>

        {/* Breakdown chips */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 160px', padding: '0.75rem 1rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
            <div style={{ fontSize: '0.6875rem', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>Free remaining</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0076A3' }} data-testid="bgv-plan-remaining">{bgv.plan_checks_remaining}</div>
          </div>
          <div style={{ flex: '1 1 160px', padding: '0.75rem 1rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
            <div style={{ fontSize: '0.6875rem', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>Prepaid (rollover)</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0076A3' }} data-testid="bgv-extra">{bgv.extra_checks}</div>
          </div>
          <div style={{ flex: '1 1 160px', padding: '0.75rem 1rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
            <div style={{ fontSize: '0.6875rem', color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700 }}>Used all-time</div>
            <div style={{ fontSize: '1.125rem', fontWeight: 700, color: '#0076A3' }} data-testid="bgv-used-total">{bgv.checks_consumed_total ?? 0}</div>
          </div>
        </div>
      </Card>

      {/* Buy BGV checks */}
      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.75rem', marginBottom: '1.5rem' }} data-testid="buy-bgv-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1.25rem' }}>
          <div style={{ maxWidth: 460 }}>
            <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.375rem' }}>Buy BGV Checks</h2>
            <p style={{ fontSize: '0.875rem', color: '#718096', marginBottom: '0.5rem' }}>
              Top up beyond your monthly free checks at <strong>{money(BGV_CHECK_PRICE[currency], currency)} per check</strong>. Purchased checks never expire.
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #ECE7DE', borderRadius: 10, overflow: 'hidden' }}>
              <button
                data-testid="bgv-qty-decrement"
                onClick={() => setBgvQty((q) => Math.max(1, (parseInt(q, 10) || 1) - 1))}
                style={{ background: '#FAF7F2', border: 'none', width: 38, height: 42, fontSize: '1.125rem', cursor: 'pointer', color: '#4A5568' }}
              >−</button>
              <input
                data-testid="bgv-qty-input"
                type="number"
                min={1}
                value={bgvQty}
                onChange={(e) => setBgvQty(e.target.value)}
                style={{ width: 60, height: 42, border: 'none', textAlign: 'center', fontSize: '0.9375rem', fontWeight: 700, color: '#0076A3', outline: 'none' }}
              />
              <button
                data-testid="bgv-qty-increment"
                onClick={() => setBgvQty((q) => (parseInt(q, 10) || 1) + 1)}
                style={{ background: '#FAF7F2', border: 'none', width: 38, height: 42, fontSize: '1.125rem', cursor: 'pointer', color: '#4A5568' }}
              >+</button>
            </div>
            <div style={{ textAlign: 'right', minWidth: 96 }}>
              <div style={{ fontSize: '0.75rem', color: '#A0AEC0' }} data-testid="bgv-qty-label">
                {Math.max(1, parseInt(bgvQty, 10) || 1).toLocaleString('en-US')} check{Math.max(1, parseInt(bgvQty, 10) || 1) === 1 ? '' : 's'}
              </div>
              <div style={{ fontSize: '1.125rem', fontWeight: 800, color: '#0076A3' }} data-testid="bgv-total">
                {money(Math.max(1, parseInt(bgvQty, 10) || 1) * BGV_CHECK_PRICE[currency], currency)}
              </div>
            </div>
            <Button
              data-testid="buy-bgv-btn"
              onClick={handlePurchaseBgv}
              disabled={bgvPurchasing}
              style={{ background: bgvPurchasing ? '#94A3B8' : '#0076A3', color: '#fff', borderRadius: 999, fontWeight: 600, padding: '0.625rem 1.5rem', cursor: bgvPurchasing ? 'wait' : 'pointer' }}
            >
              {bgvPurchasing ? 'Processing…' : 'Buy Checks'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Geo Pricing */}
      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.75rem', marginBottom: '1.5rem' }} data-testid="geo-pricing-card">
        <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem', fontWeight: 700, color: '#0076A3', marginBottom: '0.375rem' }}>Geo Pricing</h2>
        <p style={{ fontSize: '0.8125rem', color: '#718096', marginBottom: '1.25rem' }}>Per-seat subscription pricing by region.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
          {GEO_PRICING.map(gp => (
            <div key={gp.region} data-testid="geo-pricing-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', borderRadius: 10, background: '#FAF7F2', border: '1px solid #F0EBE3' }}>
              <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: '#0076A3' }}>{gp.region}</span>
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.875rem', fontWeight: 700, color: '#0076A3' }}>{gp.price} <span style={{ fontWeight: 400, color: '#94A3B8', fontSize: '0.625rem' }}>/ {gp.unit}</span></span>
            </div>
          ))}
        </div>
      </Card>

      {/* Payment history */}
      <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }} data-testid="payment-history-card">
        <h2 style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.125rem', fontWeight: 700, color: '#0076A3', marginBottom: '1rem' }}>Payment History</h2>

        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#A0AEC0' }}>Loading…</div>
        ) : payments.length === 0 ? (
          <div style={{ padding: '2.5rem 1rem', textAlign: 'center' }} data-testid="no-payments">
            <p style={{ color: '#718096', fontSize: '0.9375rem', marginBottom: '1rem' }}>No payments yet.</p>
            <Button onClick={() => navigate('/pricing')} variant="outline" style={{ borderRadius: 10 }}>View Plans</Button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#A0AEC0', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Date</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Plan</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Amount</th>
                  <th style={{ padding: '0.625rem 0.5rem' }}>Status</th>
                  <th style={{ padding: '0.625rem 0.5rem', textAlign: 'right' }}>Invoice</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const st = STATUS_STYLE[p.status] || { bg: '#F1F5F9', color: '#64748B', label: p.status };
                  return (
                    <tr key={p.order_id} style={{ borderTop: '1px solid #F0EBE3' }} data-testid={`payment-row-${p.order_id}`}>
                      <td style={{ padding: '0.875rem 0.5rem', color: '#4A5568' }}>{fmtDate(p.paid_at || p.created_at)}</td>
                      <td style={{ padding: '0.875rem 0.5rem', color: '#0076A3', fontWeight: 600 }}>{p.plan_name}</td>
                      <td style={{ padding: '0.875rem 0.5rem', color: '#0076A3' }}>{fromMinor(p.amount, p.currency || 'INR')}</td>
                      <td style={{ padding: '0.875rem 0.5rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: 999, background: st.bg, color: st.color }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: '0.875rem 0.5rem', textAlign: 'right' }}>
                        {p.status === 'paid' ? (
                          <div style={{ display: 'inline-flex', gap: '0.5rem' }}>
                            <button
                              data-testid={`download-invoice-${p.order_id}`}
                              onClick={() => handleDownload(p.order_id)}
                              disabled={downloadingId === p.order_id}
                              style={{ background: 'none', border: '1px solid #D6E4FF', color: '#0076A3', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600, padding: '0.375rem 0.75rem', borderRadius: 8 }}
                            >
                              {downloadingId === p.order_id ? 'Preparing…' : 'Download'}
                            </button>
                            <button
                              data-testid={`resend-invoice-${p.order_id}`}
                              onClick={() => handleResend(p.order_id)}
                              disabled={resendingId === p.order_id}
                              style={{ background: 'none', border: '1px solid #ECE7DE', color: '#4A5568', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600, padding: '0.375rem 0.75rem', borderRadius: 8 }}
                            >
                              {resendingId === p.order_id ? 'Sending…' : 'Resend'}
                            </button>
                          </div>
                        ) : (
                          <span style={{ color: '#CBD5E1', fontSize: '0.8125rem' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
